import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
	CampaignAnswerDraft,
	CampaignInput,
	CampaignQuestion,
	CampaignResultsParams,
	CampaignStatus,
} from '@/types/campaign'
import {
	createCampaign,
	deleteCampaign,
	deleteCampaignResponse,
	exportCampaignResponses,
	getCampaignAdmin,
	getCampaignForReader,
	getCampaignResponses,
	getCampaignResults,
	getCampaignsAdmin,
	getMyCampaigns,
	submitCampaignResponse,
	updateCampaign,
	updateCampaignStatus,
} from '../services/campaigns'

const campaignKeys = {
	all: ['campaigns'] as const,
	mine: ['campaigns', 'mine'] as const,
	reader: (id: string) => ['campaigns', 'reader', id] as const,
	admin: ['campaigns', 'admin'] as const,
	adminDetail: (id: string | null) =>
		['campaigns', 'admin', 'detail', id] as const,
	results: (id: string, params?: CampaignResultsParams) =>
		['campaigns', 'admin', 'results', id, params] as const,
	responses: (id: string, page: number) =>
		['campaigns', 'admin', 'responses', id, page] as const,
}

export function useMyCampaigns() {
	return useQuery({
		queryKey: campaignKeys.mine,
		queryFn: getMyCampaigns,
		staleTime: 60 * 1000,
	})
}

export function useCampaignForReader(id: string) {
	// sem retry: 403/404 são respostas finais (campanha de outra filial, rascunho ou inexistente)
	return useQuery({
		queryKey: campaignKeys.reader(id),
		queryFn: () => getCampaignForReader(id),
		enabled: !!id,
		retry: false,
		refetchOnWindowFocus: false,
	})
}

export function useSubmitCampaignResponse(id: string) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			questions,
			answers,
		}: {
			questions: CampaignQuestion[]
			answers: Record<string, CampaignAnswerDraft>
		}) => submitCampaignResponse(id, questions, answers),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: campaignKeys.mine })
			queryClient.invalidateQueries({ queryKey: campaignKeys.reader(id) })
		},
	})
}

export function useCampaignsAdmin() {
	return useQuery({
		queryKey: campaignKeys.admin,
		queryFn: () => getCampaignsAdmin({ limit: 100 }),
		staleTime: 0,
	})
}

export function useCampaignAdmin(id: string | null) {
	return useQuery({
		queryKey: campaignKeys.adminDetail(id),
		queryFn: () => getCampaignAdmin(id as string),
		enabled: !!id,
		staleTime: 0,
		retry: false,
	})
}

function useInvalidateCampaigns() {
	const queryClient = useQueryClient()
	return () => queryClient.invalidateQueries({ queryKey: campaignKeys.all })
}

export function useCreateCampaign() {
	const invalidate = useInvalidateCampaigns()
	return useMutation({
		mutationFn: (data: CampaignInput) => createCampaign(data),
		onSuccess: invalidate,
	})
}

export function useUpdateCampaign() {
	const invalidate = useInvalidateCampaigns()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<CampaignInput> }) =>
			updateCampaign(id, data),
		onSuccess: invalidate,
	})
}

export function useUpdateCampaignStatus() {
	const invalidate = useInvalidateCampaigns()
	return useMutation({
		mutationFn: ({ id, status }: { id: string; status: CampaignStatus }) =>
			updateCampaignStatus(id, status),
		onSuccess: invalidate,
	})
}

export function useDeleteCampaign() {
	const invalidate = useInvalidateCampaigns()
	return useMutation({
		mutationFn: (id: string) => deleteCampaign(id),
		onSuccess: invalidate,
	})
}

export function useDeleteCampaignResponse(campaignId: string) {
	const invalidate = useInvalidateCampaigns()
	return useMutation({
		mutationFn: (responseId: string) =>
			deleteCampaignResponse(campaignId, responseId),
		onSuccess: invalidate,
	})
}

export function useCampaignResults(id: string, params?: CampaignResultsParams) {
	return useQuery({
		queryKey: campaignKeys.results(id, params),
		queryFn: () => getCampaignResults(id, params),
		enabled: !!id,
		staleTime: 60 * 1000,
		// mantém a tabela na tela enquanto o filtro troca
		placeholderData: (previous) => previous,
	})
}

export function useCampaignResponses(id: string, page: number) {
	return useQuery({
		queryKey: campaignKeys.responses(id, page),
		queryFn: () => getCampaignResponses(id, { page, limit: 10 }),
		enabled: !!id,
		staleTime: 60 * 1000,
		placeholderData: (previous) => previous,
	})
}

export function useExportCampaignResponses() {
	return useMutation({
		mutationFn: ({
			id,
			...params
		}: {
			id: string
			format: 'xlsx' | 'csv'
			startDate?: string
			endDate?: string
		}) => exportCampaignResponses(id, params),
	})
}
